import { useEffect, useState } from "react";
import { Page, Frame } from "@shopify/polaris";
import { useNavigate } from "react-router-dom";
import createApp from "@shopify/app-bridge";
import { getSessionToken } from "@shopify/app-bridge-utils";
import useMessage from "../../hooks/useMessage";
import { handleError } from "../../utils/errorHandling";
import { getSettingsV2 } from "../../requests";
import { getAppConfig, getShop } from "../../utils/misc";

import { defaultValuesV2 } from "./config";
import { getInitialValuesV2, isFormValuesV2 } from "./utils";

import styles from "./Settings.module.css";
import SettingsV2Component from "./components/SettingsV2";
import SettingsComponent from "./components/Settings";

const appConfig = getAppConfig();

function Settings() {
  const navigate = useNavigate();

  const { toastMarkup, bannerMarkup, showMessage } = useMessage();
  const [isLoading, setIsLoading] = useState(false);
  const [initialValuesV2, setInitialValuesV2] = useState(defaultValuesV2);
  const [shop, setShop] = useState("");
  const [isMerchantUsingV2, setIsMerchantUsingV2] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);

        // Get shop from session token (embedded apps don't have shop in URL)
        if (appConfig) {
          const app = createApp(appConfig);
          const token = await getSessionToken(app)
            .then((token) => token)
            .catch((err) => {
              handleError(err, showMessage, navigate);
              return "";
            });

          const shopDomain = getShop(token);
          setShop(shopDomain);

          const { data } = await getSettingsV2(shopDomain);

          if (isFormValuesV2(data)) {
            setInitialValuesV2(getInitialValuesV2(data));
            // Use the backend-computed flag to determine which UI to show
            setIsMerchantUsingV2(data.is_merchant_using_v2 ?? false);
          }
        }
      } catch (err) {
        handleError(err, showMessage, navigate);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <Frame>
      <Page title="Settings">
        <div className={styles.bannerContainer}>{bannerMarkup}</div>
        {/* Show V2 settings for merchants using V2 (CUSTOM stores or installed after rollout date) */}
        {isMerchantUsingV2 ? (
          <SettingsV2Component
            initialValues={initialValuesV2}
            showMessage={showMessage}
            setIsLoading={setIsLoading}
            isLoading={isLoading}
            shop={shop}
          />
        ) : (
          <SettingsComponent
            showMessage={showMessage}
            setIsLoading={setIsLoading}
            isLoading={isLoading}
          />
        )}
      </Page>
      {toastMarkup}
    </Frame>
  );
}

export default Settings;
